import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import GestionServicioSistemasService from "../axios_services/gestionServicioSistemas.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonInsert, ButtonUpdate, ButtonDelete } from '../components/Buttons/Buttons';
import ModalGestionServicioSistemas from '../components/Modal/ModalGestionServicioSistemas';
import ArchivoService from '../axios_services/archivos.service';
import { FileMinus } from "lucide-react";
import './GestionServicioSistemas.css';

function GestionServicioSistemas() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const initialFetchDone = useRef(false);

    const [estadosGestion] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 41));
    const [prioridadGestion] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 42));
    const [nombreAreas] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 29));
    const [usuariosCatalog] = useState(state.user?.personal || []);
    const userProfile = state.catalogos?.perfiles?.find(p => String(p.perfil_id) === String(state.user?.perfil_id));
    const areaId = userProfile?.area_id || state.user?.area_id;

    const [periodo, setPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [estadoFiltro, setEstadoFiltro] = useState('');

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialPageData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadInitialPageData = async () => {
        setIsLoading(true);
        try {
            const response = await GestionServicioSistemasService.getGestionServicio(periodo, estadoFiltro);
            setRegistros(response.data || []);
        } catch (error) {
            console.error(error);
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = async () => {
        await loadInitialPageData();
    };

    const handleNuevoRegistro = () => {
        setSelectedRegistro(null);
        setIsModalOpen(true);
    };

    const handleEditarRegistro = (registro) => {
        setSelectedRegistro(registro);
        setIsModalOpen(true);
    };

    const handleCloseModal = (shouldReload = false) => {
        setIsModalOpen(false);
        if (shouldReload === true) {
            loadInitialPageData();
        }
    };

    const handleModalClosed = () => {
        setSelectedRegistro(null);
    };

    const handleSaveRegistro = async (registroData) => {
        setIsLoading(true);
        // El modal se encargará de cerrarse y llamar a loadInitialPageData cuando termine de subir archivos
        try {
            let response;
            if (registroData.gestion_servicio_id) {
                response = await GestionServicioSistemasService.updateGestionServicio(registroData);
            } else {
                response = await GestionServicioSistemasService.insertGestionServicio(registroData);
            }
            return response.data || response;
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        }
    };

    const handleEliminarRegistro = async (registro) => {
        if (registro.estado == 2) {
            alert('No se puede eliminar un registro en estado "Atendido".');
            return;
        }

        if (!window.confirm('¿Está seguro de eliminar este requerimiento?')) return;
        setIsLoading(true);
        try {
            await GestionServicioSistemasService.deleteGestionServicio({ gestion_servicio_id: registro.gestion_servicio_id });
            await loadInitialPageData();
        } catch (error) {
            alert(error.response?.data?.message || 'Ocurrió un error al eliminar.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '-';
        return fecha.split('T')[0];
    };

    const handleVerDocumento = async (gestion_servicio_id) => {
        try {
            const documento = await ArchivoService.getDocumentoServicioSistemas(gestion_servicio_id);
            if (documento && documento.url_completa) {
                window.open(documento.url_completa, "_blank", "noopener,noreferrer");
            } else {
                alert("Este registro no tiene un documento adjunto.");
            }
        } catch (error) {
            alert("Ocurrió un error al intentar abrir el documento adjunto.");
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    const canManageOrRegister = [8, 5, 1, 6, 9, 11, 12, 15, 21, 22, 24, 25, 26].includes(Number(state.user?.perfil_id));

    const getRowStyle = (estado) => {
        if (estado == 2) return { backgroundColor: '#fef3c7' }; // Amarillo suave (En Proceso)
        if (estado == 3) return { backgroundColor: '#dcfce7' }; // Verde suave (Finalizado)
        return {};
    };

    return (
        <div className="caja-flujo-container">
            <h1>GESTIÓN DE SERVICIOS</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={periodo}
                        onChange={(e) => setPeriodo(e.target.value)}
                    />
                </div>

                <div className="campo-fecha">
                    <label>Estado:</label>
                    <select
                        value={estadoFiltro}
                        onChange={(e) => setEstadoFiltro(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos los estados</option>
                        {estadosGestion?.map((estado) => (
                            <option key={estado.tipo_id} value={estado.tipo_id}>
                                {estado.descripcion}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <ButtonSearch
                onClick={handleSearch}
                isLoading={isLoading}
            />

            <div className="nuevo-movimiento-section">
                {canManageOrRegister && (
                    <ButtonInsert onClick={handleNuevoRegistro} />
                )}
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th style={{ width: '200px', minWidth: '200px' }}>Solicitante</th>
                            <th>Área</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Fecha Registro</th>
                            <th style={{ width: '350px', minWidth: '350px' }}>Requerimiento</th>
                            <th style={{ width: '100px', minWidth: '100px', textAlign: 'center' }}>Archivo ref.</th>
                            <th style={{ width: '90px', minWidth: '90px', textAlign: 'center' }}>Hora aprox.</th>
                            <th>Prioridad</th>
                            <th style={{ width: '200px', minWidth: '200px' }}>Encargado</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Fecha Inicio</th>
                            <th>Comentario</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>Fecha Entrega</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length > 0 ? (
                            registros.map((registro) => {
                                const usuarioSolicitante = usuariosCatalog.find(u => u.usuario_id == registro.usuario_id_solicitante);
                                const areaDesc = nombreAreas.find(a => a.tipo_id == registro.area_id)?.descripcion || registro.area_id;
                                const prioridadLabel = prioridadGestion.find(p => p.tipo_id == registro.prioridad_id)?.descripcion;
                                const encargadoName = usuariosCatalog.find(u => u.usuario_id == registro.encargado_id)?.nombre_completo_usuario;
                                const estadoLabel = estadosGestion.find(e => e.tipo_id == registro.estado)?.descripcion;

                                const rowStyle = getRowStyle(registro.estado);

                                return (
                                    <tr key={registro.gestion_servicio_id} style={rowStyle}>
                                        <td>{usuarioSolicitante ? usuarioSolicitante.nombre_completo_usuario.toUpperCase() : 'SISTEMA'}</td>
                                        <td>{areaDesc}</td>
                                        <td>{formatFecha(registro.fecha_registro)}</td>
                                        <td style={{ whiteSpace: 'pre-wrap' }}>
                                            {registro.requerimiento}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                {registro.tiene_documento_referencia === '1' || registro.tiene_documento_referencia === 1 ? (
                                                    <button
                                                        onClick={() => handleVerDocumento(registro.gestion_servicio_id)}
                                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#3cb167', display: 'flex', alignItems: 'center' }}
                                                        title="Ver documento adjunto"
                                                    >
                                                        <FileMinus size={20} />
                                                    </button>
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <FileMinus size={20} style={{ color: '#f87171', opacity: 0.5 }} title="Sin documento" />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {registro.horas_estimadas != null && registro.horas_estimadas !== '' ? registro.horas_estimadas : '-'}
                                        </td>
                                        <td>{prioridadLabel || '-'}</td>
                                        <td>{encargadoName ? encargadoName.toUpperCase() : '-'}</td>
                                        <td>{formatFecha(registro.fecha_inicio)}</td>
                                        <td style={{ whiteSpace: 'pre-wrap', minWidth: '200px' }}>
                                            {registro.comentario || '-'}
                                        </td>
                                        <td>{formatFecha(registro.fecha_entrega)}</td>
                                        <td>{estadoLabel || registro.estado}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                {canManageOrRegister && (
                                                    <ButtonUpdate onClick={() => handleEditarRegistro(registro)} />
                                                )}
                                                {Number(state.user?.perfil_id) === 8 && (
                                                    <ButtonDelete onClick={() => handleEliminarRegistro(registro)} />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={13} className="text-center text-gray-500 py-4">
                                    No hay requerimientos registrados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalGestionServicioSistemas
                isOpen={isModalOpen}
                registro={selectedRegistro}
                onSave={handleSaveRegistro}
                onClose={handleCloseModal}
                onModalClosed={handleModalClosed}
                usuarioId={state.user?.usuario_id}
                perfilId={state.user?.perfil_id}
                areaId={areaId}
                estadosGestion={estadosGestion}
                prioridadGestion={prioridadGestion}
                usuariosCatalog={usuariosCatalog}
                perfilesCatalog={state.catalogos?.perfiles || []}
            />
        </div>
    );
}

export default GestionServicioSistemas;
